import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { useCodeSearch } from '../hooks/useCodeSearch';
import { minQueryLengthFor, type Code, type CodeType } from '../services/codesService';

export interface CodeAutocompleteProps {
  /** Current committed value — the code string. */
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  /**
   * Which coding set to suggest from. Omit to disable suggestions entirely, in which case this
   * behaves as a plain text field — which is how ASA rows stay free-text until that sourcing
   * question is settled.
   */
  codeType?: CodeType;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  width?: number | string;
  /**
   * Element the dropdown is aligned and sized to. The code box is deliberately narrow so that
   * Modifiers can sit beside it, and a box that narrow truncates every description away — so a
   * caller that wraps this in a wider row passes that row here. Omit it and the dropdown matches
   * the input, as MUI does by default.
   */
  popperAnchorEl?: HTMLElement | null;
}

/**
 * A code input with type-ahead over the local Quick Base mirror (feature 046).
 *
 * Free text is always accepted, loaded or not: a newly-issued code that Quick Base has not
 * published yet must still be billable, so nothing here may reject an off-list value.
 */
function CodeAutocompleteInner({
  value,
  onChange,
  onBlur,
  codeType,
  label,
  placeholder,
  disabled,
  error,
  helperText,
  width = 140,
  popperAnchorEl,
}: CodeAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value ?? '');
  const isFocusedRef = useRef(false);

  // Whether the text in the box came from the biller rather than from the form.
  //
  // A row loaded from an existing encounter, or appended by accepting an AI suggestion, mounts
  // already holding a code. Searching for it would be pure waste — one request per row on page
  // load, for a value nobody is looking up — and the two degraded helper lines are answers to a
  // question the biller never asked. Both are therefore gated on this rather than on the text
  // being non-empty. Picking a suggestion clears it for the same reason: the code is now chosen,
  // not being searched for.
  const [userEdited, setUserEdited] = useState(false);

  // MUI decides whether to open the listbox at the moment of the input event. With an async
  // source the options arrive a few hundred milliseconds later, by which time it has already
  // decided not to — so the dropdown appeared one keystroke late, and typing a fourth character
  // was what surfaced the third character's results. Driving `open` ourselves means it opens when
  // there is something to show.
  const [popupOpen, setPopupOpen] = useState(false);

  const { codes, loading, status } = useCodeSearch(codeType, userEdited ? inputValue : '');

  // Reflect a value changed from outside — react-hook-form's reset() after an encounter loads,
  // "Submit Another Claim", or a feature-036 AI suggestion accepted into an existing row. Without
  // this the input keeps showing whatever was last typed while the form holds something else,
  // which is silent and looks like data loss.
  //
  // Skipped while focused, for the same reason LabeledTimePicker skips it: re-seeding
  // mid-keystroke would fight the biller's typing.
  useEffect(() => {
    if (isFocusedRef.current) return;
    setInputValue(value ?? '');
    // Arrived from the form, so it is not a query.
    setUserEdited(false);
  }, [value]);

  // NOT_READY means the set cannot help — no dropdown at all, so the field reads exactly as it
  // did before this feature. Not an error, not disabled, no alert: the biller can still type.
  const suggestionsAvailable = !!codeType && status === 'READY';
  const options = useMemo<Code[]>(
    () => (suggestionsAvailable ? codes : []),
    [suggestionsAvailable, codes],
  );

  // Gated on the same per-set floor as the request itself, so a query below it shows no helper
  // line at all rather than "No matching codes" for a search that was never run.
  const hasQuery = userEdited && (inputValue ?? '').trim().length >= minQueryLengthFor(codeType);

  /*
    Three states, and the two degraded ones must not be confusable.

    MUI's own `noOptionsText` is no use here: `freeSolo` suppresses it entirely, on the reasoning
    that the typed value is itself a valid answer. True, but it leaves a loaded set with no
    matches looking identical to a set that never loaded — which is the exact conflation the spec
    separates READY-with-zero-codes from NOT_READY to avoid. So both are said out loud, quietly,
    in the helper line.

    "unavailable", never "still loading": the not-ready state has two causes, and when the kill
    switch is the cause nothing is loading and nothing will change until an operator flips it
    back. Telling the biller to wait would be a lie half the time.
  */
  const noMatches = suggestionsAvailable && !loading && hasQuery && codes.length === 0;
  const suggestionsUnavailable = !!codeType && status === 'NOT_READY' && hasQuery && !loading;

  // A caller's own message always wins — it will be a validation error, which outranks either of
  // these advisory lines.
  const effectiveHelperText =
    helperText
    ?? (noMatches
      ? 'No matching codes'
      : suggestionsUnavailable
        ? 'Code suggestions are unavailable'
        : undefined);

  const handleInputChange = useCallback(
    (_event: unknown, next: string) => {
      setUserEdited(true);
      setInputValue(next);
      // Commit as the biller types. Selecting an option is a convenience, not the only way to
      // produce a value, so the field must never require one.
      onChange(next);
    },
    [onChange],
  );

  const handleChange = useCallback(
    (_event: unknown, selected: string | Code | null) => {
      // Only the code string is committed — never `code — description`. The encounter wire
      // contract is unchanged by this feature.
      const next = typeof selected === 'string' ? selected : (selected?.code ?? '');
      // Chosen, not searched for — so no follow-up request and no helper line about it.
      setUserEdited(false);
      setPopupOpen(false);
      setInputValue(next);
      onChange(next);
    },
    [onChange],
  );

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement> & { key?: string }, option: Code) => {
      const { key, ...rest } = props;
      return (
        <Box component="li" key={key} {...rest} sx={{ display: 'block !important', py: 0.75 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.primary' }}>
            {option.code}
          </Typography>
          {/*
            Truncated with CSS, never with `description.slice(...)`. CPT descriptions average 124
            characters and reach 1,741, so they cannot render as a dropdown line — but cutting the
            string would hide the rest from screen readers and from copy as well as from sighted
            users. The full text stays in the DOM and `title` surfaces it on hover.

            A native `title` rather than MUI `Tooltip`: these options already live inside the
            Autocomplete's popper, and a second portal per option fights the listbox.
          */}
          <Typography
            title={option.description}
            sx={{
              fontSize: 12,
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {option.description}
          </Typography>
        </Box>
      );
    },
    [],
  );

  return (
    <Autocomplete<Code, false, false, true>
      freeSolo
      openOnFocus={false}
      // `popupOpen` records intent (the biller is typing); the options check decides whether
      // there is anything worth showing. Keeping them separate is what lets late-arriving
      // options open the listbox without a further keystroke.
      open={popupOpen && options.length > 0}
      onOpen={() => setPopupOpen(true)}
      onClose={() => setPopupOpen(false)}
      disabled={disabled}
      options={options}
      // The server already filtered and ranked; re-filtering client-side would drop matches the
      // description search found and re-order the tiers the endpoint deliberately established.
      filterOptions={(x) => x}
      loading={loading}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.code)}
      isOptionEqualToValue={(option, selected) => option.code === selected.code}
      renderOption={renderOption}
      sx={{ width }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={undefined}
          placeholder={placeholder}
          onFocus={() => { isFocusedRef.current = true; }}
          onBlur={() => {
            isFocusedRef.current = false;
            setPopupOpen(false);
            onBlur?.();
          }}
          error={error}
          helperText={effectiveHelperText}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              'aria-label': label,
              autoComplete: 'off',
            },
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      slotProps={{
        listbox: {
          // MUI wires the combobox ARIA roles, but not a spoken loading state or result count —
          // a sighted user gets the spinner and everyone else gets nothing without this.
          'aria-busy': loading,
        },
        // Both halves are needed: MUI writes the input's own width onto the popper as an inline
        // style, so re-pointing the anchor alone would move the listbox without widening it.
        ...(popperAnchorEl && {
          popper: { anchorEl: popperAnchorEl, style: { width: popperAnchorEl.clientWidth } },
        }),
      }}
    />
  );
}

export const CodeAutocomplete = memo(CodeAutocompleteInner);

export default CodeAutocomplete;
